import { useStudyContext } from 'common/contexts/StudyContext';
import { useTracking } from 'common/hooks/tracking';
import React, { useCallback, useMemo } from 'react';
import { CREATE_NEW, CREATE_SEGMENT_HASH, DataTestId, GUEST } from 'settings/constants';
import { MAIN_ROUTES } from 'settings/routes';
import Icon from 'common/components/Icon';
import AddButton from 'common/components/SidebarNav/AddButton';
import BottomNav from 'common/components/SidebarNav/BottomNav';
import { IconButtonContainer, SidebarContainer, TopNavContainer, Logo, TopActionsGroupContainer } from './styled';
import { useTranslation } from 'react-i18next';
import { accountDetailsSelector, permissionsSelector, roleSelector } from 'common/state/account';
import { useRecoilValue } from 'recoil';
import { saveRedirectPath } from 'utils/auth';
import { TRACKING_EVENTS } from 'common/types/tracking';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICONS } from 'icons/iconsMap';
import { useAiChatBotEnabled } from 'common/hooks/features';
import { useCustomKpiStudies } from 'common/queries/customKpis';
import { catalogueSelectionPath } from 'modules/kpis/catalogue';

enum NavOptions {
  CHART = 'chart',
  DASHBOARD = 'dashboard',
  SEGMENT = 'segment',
  FOLDER = 'folder',
  CUSTOM_KPI = 'customKpi',
}
interface SidebarNavProps {
  onCreateFolder: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ onCreateFolder }) => {
  const { pathname } = useLocation();
  const { trackEvent } = useTracking();
  const { toggleStudySelectionModal } = useStudyContext();
  const { t } = useTranslation();
  const { name: roleName } = useRecoilValue(roleSelector);
  const { user } = useRecoilValue(accountDetailsSelector);
  const { isEnabled } = useAiChatBotEnabled();
  const { internal: isInternalUser } = useRecoilValue(permissionsSelector);
  const isKpiCatalogueEnabled = useCustomKpiStudies().length > 0;
  const navigate = useNavigate();

  const isNavButtonActive = React.useCallback(
    (path?: string) => {
      return path && pathname.includes(path);
    },
    [pathname],
  );

  const handleTrackEvent = useCallback(
    (eventName: string, screen?: string) => {
      trackEvent(eventName, {
        internal: isInternalUser,
        account: user.name,
        userId: user.uuid,
        screen,
      });
    },
    [isInternalUser, user, trackEvent],
  );

  const topNavItems = useMemo(
    () => [
      {
        icon: 'home' as keyof typeof ICONS,
        label: t('nav.home'),
        disabled: true,
        testId: DataTestId.HOME_PAGE,
      },
      {
        icon: 'apps' as keyof typeof ICONS,
        label: t('nav.dashboards'),
        action: () => {
          navigate(MAIN_ROUTES.DASHBOARDS);
        },
        url: MAIN_ROUTES.DASHBOARDS,
        testId: DataTestId.DASHBOARDS_PAGE,
      },
      {
        icon: 'column-chart' as keyof typeof ICONS,
        label: t('nav.charts'),
        action: () => {
          navigate(MAIN_ROUTES.CHARTS);
        },
        url: MAIN_ROUTES.CHARTS,
        testId: DataTestId.CHARTS_PAGE,
      },
      {
        icon: 'user-group' as keyof typeof ICONS,
        label: t('nav.segments'),
        action: () => {
          navigate(`/segments/latana`);
        },
        url: 'segments',
        testId: DataTestId.SEGMENTS_PAGE,
      },
      ...(isKpiCatalogueEnabled
        ? [
            {
              icon: 'gauge' as keyof typeof ICONS,
              label: t('nav.kpiCatalogue'),
              action: () => {
                navigate('/kpi-catalogue');
              },
              url: 'kpi-catalogue',
              testId: DataTestId.KPI_CATALOGUE_PAGE,
            },
          ]
        : []),
      /** This condition should be removed once internal testing is finished */
      ...(isEnabled || isInternalUser
        ? [
            {
              icon: 'chat' as keyof typeof ICONS,
              label: t('nav.dataAssistant'),
              action: () => {
                navigate(`/chat`);
              },
              url: 'chat',
              testId: DataTestId.CHATBOT_PAGE,
            },
          ]
        : []),
    ],
    [t, navigate, isInternalUser, isEnabled, isKpiCatalogueEnabled],
  );

  const addButtonSelectHandle = useCallback(
    selectedOption => {
      // Save previous url path in case user performs some action that changes the url
      saveRedirectPath(window.location.pathname);
      handleTrackEvent(`${TRACKING_EVENTS.NAV_BAR_ADD_ITEM_CLICK}${selectedOption[0]}`);
      switch (selectedOption[0]) {
        case NavOptions.CHART:
          toggleStudySelectionModal(true);
          break;
        case NavOptions.DASHBOARD:
          navigate(`/dashboard/${CREATE_NEW}`);
          break;
        case NavOptions.SEGMENT:
          toggleStudySelectionModal(true, CREATE_SEGMENT_HASH);
          break;
        case NavOptions.FOLDER:
          onCreateFolder();
          break;
        // The catalogue opens the builder in the current study, with a study picker above it.
        case NavOptions.CUSTOM_KPI:
          navigate(catalogueSelectionPath({ kind: 'new' }));
          break;
        default:
          break;
      }
    },
    [toggleStudySelectionModal, onCreateFolder, handleTrackEvent, navigate],
  );

  const renderTopNavigation = useMemo(() => {
    return (
      <TopNavContainer>
        <Logo src='/logo.png' alt='Logo' />
        <TopActionsGroupContainer>
          {roleName.toLowerCase() !== GUEST && <AddButton onSelect={addButtonSelectHandle} canCreateCustomKpi={isKpiCatalogueEnabled} />}
          {topNavItems.map(
            item =>
              !item.disabled && (
                <IconButtonContainer
                  key={item.label}
                  data-active={isNavButtonActive(item?.url)}
                  data-hover={item.label}
                  onClick={item?.action}
                  data-testid={item.testId}
                >
                  <Icon icon={item.icon} color='gray600' size={20} />
                </IconButtonContainer>
              ),
          )}
        </TopActionsGroupContainer>
      </TopNavContainer>
    );
  }, [addButtonSelectHandle, isNavButtonActive, topNavItems, roleName, isKpiCatalogueEnabled]);

  return (
    <SidebarContainer data-testid={DataTestId.SIDEBAR}>
      {renderTopNavigation}
      <BottomNav />
    </SidebarContainer>
  );
};

export default React.memo(SidebarNav);
